import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { apiRequest } from "../../helpers/api";
import { showError, showSuccess } from "../../components/ToastNotifications";
import { useAdminAccess } from "../AdminAccessContext";
import "./CreateUserDialog.css";

const SUPER_ADMIN = "super";
const passwordRule = "10 to 72 characters with at least one letter and one number.";
const isStrongPassword = (value) => value.length >= 10 && value.length <= 72 && /[A-Za-z]/.test(value) && /\d/.test(value);
const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Readable random password that always satisfies the server rule (letters + digits, 14 chars).
const generatePassword = () => {
  const letters = "abcdefghjkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ";
  const digits = "23456789";
  const pool = letters + digits;
  const random = new Uint32Array(14);
  window.crypto.getRandomValues(random);
  const chars = Array.from(random, (value) => pool[value % pool.length]);
  chars[0] = letters[random[0] % letters.length];
  chars[13] = digits[random[13] % digits.length];
  return chars.join("");
};

const emptyForm = { accountType: "customer", firstName: "", lastName: "", email: "", phone: "", setup: "invite", password: "", customerRoleId: "", staffRoleId: "" };

const CreateUserDialog = ({ onClose, onCreated }) => {
  const { access, can } = useAdminAccess();
  const canCreateCustomer = can("admin.users.create");
  const canInviteStaff = can("admin.access_control.create");
  const canAssignCustomerRole = can("admin.access_control.edit");
  const canLoadRoles = can("admin.access_control.view");
  const isSuperAdmin = Boolean(access?.isSuperAdmin);

  const [form, setForm] = useState({ ...emptyForm, accountType: canCreateCustomer ? "customer" : "staff" });
  const [roles, setRoles] = useState([]);
  const [rolesError, setRolesError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const firstField = useRef(null);
  const dialogRef = useRef(null);

  useEffect(() => {
    if (!canLoadRoles) return undefined;
    let active = true;
    apiRequest("/api/admin/access-control/roles")
      .then((response) => { if (active) setRoles(response.roles || []); })
      .catch((requestError) => { if (active) setRolesError(requestError.message || "Unable to load roles."); });
    return () => { active = false; };
  }, [canLoadRoles]);

  useEffect(() => { firstField.current?.focus(); }, [result]);

  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = previous; };
  }, []);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape" && !saving) onClose();
      if (event.key !== "Tab" || !dialogRef.current) return;
      const focusable = dialogRef.current.querySelectorAll("button:not([disabled]), input:not([disabled]), select:not([disabled]), a[href]");
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose, saving]);

  const customerRoles = roles.filter((role) => role.appliesTo?.customers);
  const staffRoles = roles.filter((role) => role.appliesTo?.staff && role.canManage);
  const isStaff = form.accountType === "staff";
  const staffRoleValue = form.staffRoleId || (staffRoles[0] ? String(staffRoles[0].id) : isSuperAdmin ? SUPER_ADMIN : "");
  const update = (field) => (event) => setForm((current) => ({ ...current, [field]: event.target.value }));

  const validate = () => {
    if (!form.firstName.trim()) return "Enter a first name.";
    if (!emailPattern.test(form.email.trim())) return "Enter a valid email address.";
    if (isStaff && !staffRoleValue) return "Choose a role for this team member.";
    if (!isStaff && form.setup === "password" && !isStrongPassword(form.password)) return `Password must be ${passwordRule}`;
    return "";
  };

  const submit = async (event) => {
    event.preventDefault();
    const problem = validate();
    if (problem) { setError(problem); return; }
    setSaving(true);
    setError("");
    try {
      const base = { firstName: form.firstName.trim(), lastName: form.lastName.trim(), email: form.email.trim() };
      const response = isStaff
        ? await apiRequest("/api/admin/access-control/members", { method: "POST", body: JSON.stringify({ ...base, roleId: staffRoleValue === SUPER_ADMIN ? SUPER_ADMIN : Number(staffRoleValue) }) })
        : await apiRequest("/api/admin/users", {
          method: "POST",
          body: JSON.stringify({
            ...base,
            phone: form.phone.trim(),
            setup: form.setup,
            ...(form.setup === "password" ? { password: form.password } : {}),
            roleId: form.customerRoleId ? Number(form.customerRoleId) : null
          })
        });
      showSuccess(response.message);
      setResult({ email: base.email, isStaff, setupUrl: response.setupUrl, password: !isStaff && form.setup === "password" ? form.password : "" });
      onCreated?.({ isStaff, response });
    } catch (requestError) {
      setError(requestError.message || "Unable to create this account.");
    } finally {
      setSaving(false);
    }
  };

  const copy = (value, label) => navigator.clipboard?.writeText(value).then(() => showSuccess(`${label} copied.`), () => showError("Copy it manually."));
  const createAnother = () => { setResult(null); setForm({ ...emptyForm, accountType: form.accountType }); setShowPassword(false); };

  // Rendered on <body> so page layout (sticky header, sidebar, animated containers) can never cover it.
  return createPortal(
    <div className="cu-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget && !saving) onClose(); }}>
      <section ref={dialogRef} className="cu-dialog" role="dialog" aria-modal="true" aria-labelledby="cu-title">
        <header className="cu-head">
          <span className="cu-head-icon" aria-hidden="true"><i className={`ti ${result ? "ti-circle-check" : "ti-user-plus"}`} /></span>
          <div><p>{result ? "Account created" : "New account"}</p><h2 id="cu-title">{result ? "User created" : "Create user"}</h2></div>
          <button type="button" className="cu-close" aria-label="Close" onClick={onClose} disabled={saving}><i className="ti ti-x" aria-hidden="true" /></button>
        </header>

        {result ? <div className="cu-body">
          <p className="cu-lead">{result.isStaff ? "The team member was added to the admin portal." : "The customer account is active and verified."} <strong>{result.email}</strong></p>
          {result.setupUrl && <>
            <p className="cu-hint">The setup email could not be sent. Share this one-time link securely; it expires in 72 hours.</p>
            <div className="cu-copy"><input ref={firstField} readOnly aria-label="Account setup link" value={result.setupUrl} onFocus={(event) => event.target.select()} /><button type="button" className="cu-button is-primary" onClick={() => copy(result.setupUrl, "Setup link")}><i className="ti ti-copy" aria-hidden="true" />Copy</button></div>
          </>}
          {result.password && <>
            <p className="cu-hint">Share the initial password securely. It won't be shown again.</p>
            <div className="cu-copy"><input ref={firstField} readOnly aria-label="Initial password" value={result.password} onFocus={(event) => event.target.select()} /><button type="button" className="cu-button is-primary" onClick={() => copy(result.password, "Password")}><i className="ti ti-copy" aria-hidden="true" />Copy</button></div>
          </>}
          {!result.setupUrl && !result.password && <p className="cu-hint">A setup link was emailed. It expires in 72 hours.</p>}
          {result.isStaff && <p className="cu-hint">Team members are managed in <Link to="/admin/access-control" onClick={onClose}>Roles &amp; permissions</Link>.</p>}
          <footer className="cu-actions">
            <button type="button" className="cu-button" onClick={createAnother}>Create another</button>
            <button type="button" ref={result.setupUrl || result.password ? undefined : firstField} className="cu-button is-primary" onClick={onClose}>Done</button>
          </footer>
        </div> : <form className="cu-body" onSubmit={submit} noValidate>
          {canCreateCustomer && canInviteStaff && <fieldset className="cu-segment">
            <legend>Account type</legend>
            <div className="cu-choices">{[["customer", "ti-user", "Customer", "Workspace account"], ["staff", "ti-shield-lock", "Admin team member", "Admin portal access"]].map(([value, icon, label, hint]) => (
              <label key={value} className={form.accountType === value ? "is-selected" : ""}>
                <input type="radio" name="accountType" value={value} checked={form.accountType === value} onChange={update("accountType")} />
                <i className={`ti ${icon}`} aria-hidden="true" /><span><strong>{label}</strong><small>{hint}</small></span>
              </label>
            ))}</div>
          </fieldset>}

          <div className="cu-row">
            <label className="cu-field"><span className="cu-label">First name</span><input ref={firstField} value={form.firstName} onChange={update("firstName")} maxLength={80} autoComplete="off" required /></label>
            <label className="cu-field"><span className="cu-label">Last name</span><input value={form.lastName} onChange={update("lastName")} maxLength={80} autoComplete="off" /></label>
          </div>
          <div className="cu-row">
            <label className="cu-field"><span className="cu-label">Email</span><input type="email" value={form.email} onChange={update("email")} maxLength={254} autoComplete="off" required /></label>
            {!isStaff && <label className="cu-field"><span className="cu-label">Phone <small>(optional)</small></span><input type="tel" value={form.phone} onChange={update("phone")} maxLength={40} autoComplete="off" /></label>}
          </div>

          {isStaff ? <label className="cu-field"><span className="cu-label">Admin role</span>
            <select aria-label="Admin role" value={staffRoleValue} onChange={update("staffRoleId")}>
              {!staffRoleValue && <option value="">No assignable roles</option>}
              {staffRoles.map((role) => <option key={role.id} value={String(role.id)}>{role.name}</option>)}
              {isSuperAdmin && <option value={SUPER_ADMIN}>Super admin (full access)</option>}
            </select>
            <small className="cu-field-help">An invitation email lets them set their own password.</small>
          </label> : <>
            <label className="cu-field"><span className="cu-label">Workspace role</span>
              <select aria-label="Workspace role" value={form.customerRoleId} onChange={update("customerRoleId")} disabled={!canAssignCustomerRole}>
                <option value="">Full access (every feature their plan includes)</option>
                {customerRoles.map((role) => <option key={role.id} value={String(role.id)}>{role.name}</option>)}
              </select>
              <small className="cu-field-help">{!canAssignCustomerRole ? "Your role can't assign workspace roles, so the account gets full access." : customerRoles.length ? "A role limits the customer to that role's workspace permissions." : "Create a role with user-module permissions in Roles & permissions to limit access."}</small>
            </label>

            <fieldset className="cu-options">
              <legend>Password</legend>
              <div className="cu-choices"><label><input type="radio" name="setup" value="invite" checked={form.setup === "invite"} onChange={update("setup")} /><span><strong>Email a setup link</strong><small>The customer chooses their own password.</small></span></label>
              <label><input type="radio" name="setup" value="password" checked={form.setup === "password"} onChange={update("setup")} /><span><strong>Set a password now</strong><small>You share it with the customer.</small></span></label></div>
            </fieldset>
            {form.setup === "password" && <div className="cu-field">
              <label htmlFor="cu-password" className="cu-label">Initial password</label>
              <span className="cu-password">
                <input id="cu-password" type={showPassword ? "text" : "password"} value={form.password} onChange={update("password")} maxLength={72} autoComplete="new-password" aria-describedby="cu-password-help" />
                <button type="button" onClick={() => setShowPassword((value) => !value)} aria-label={showPassword ? "Hide password" : "Show password"}><i className={`ti ${showPassword ? "ti-eye-off" : "ti-eye"}`} aria-hidden="true" /></button>
                <button type="button" onClick={() => { setForm((current) => ({ ...current, password: generatePassword() })); setShowPassword(true); }}>Generate</button>
              </span>
              <small id="cu-password-help" className={`cu-field-help${form.password && !isStrongPassword(form.password) ? " is-error" : ""}`}>{passwordRule}</small>
            </div>}
          </>}

          {rolesError && <p className="cu-hint">{rolesError}</p>}
          {error && <p className="cu-error" role="alert"><i className="ti ti-alert-circle" aria-hidden="true" />{error}</p>}
          <footer className="cu-actions">
            <button type="button" className="cu-button" onClick={onClose} disabled={saving}>Cancel</button>
            <button type="submit" className="cu-button is-primary" disabled={saving}>{saving ? "Creating..." : isStaff ? "Send invitation" : "Create user"}</button>
          </footer>
        </form>}
      </section>
    </div>,
    document.body
  );
};

export default CreateUserDialog;
